'use client'

import React from 'react'
import { RenderedService, ServiceItem } from '@/types/interfaces/service-item'
import type { Sku } from '@/types/interfaces/sku'
import { RenderedServicesType, ServiceItemCardType } from '@/types/enums/ServiceItem'
import { PageLocation } from '@nile/mantine-theme/analytics'
import { getCategoryPageData } from '@/lib/hardcoded-data/CategoryPages/getCategoryPageData'
import type { CategoryPageData } from '@/lib/hardcoded-data/CategoryPages/getCategoryPageData'
import type { ResolvedCategoryHeaderDisplay } from '@/lib/hardcoded-data/CategoryPages/resolveCategoryHeaderDisplay'
import { findRegistryEntry } from '../componentRegistry'

export interface StoreBodyRendererProps {
    service: RenderedService
    categoryName: string
    primaryService?: ServiceItem | null
    primarySku?: Sku | null
    resolvedCategoryHeader?: ResolvedCategoryHeaderDisplay | null
    /** Eager-load the first featured-category card background (page LCP candidate). */
    prioritizeFeaturedCategoryLcp?: boolean
}

const commonProps = (categoryName: string) => ({
    pageLocation: PageLocation.CATEGORY as PageLocation,
    additionalInformation: { categoryName },
})

export default function StoreBodyRenderer({
    service,
    categoryName,
    primaryService,
    primarySku,
    resolvedCategoryHeader,
    prioritizeFeaturedCategoryLcp = false,
}: StoreBodyRendererProps) {
    if (!service?.type) return null
    const slug = typeof categoryName === 'string' ? categoryName : ''
    const props = commonProps(slug)
    const categoryData = getCategoryPageData(slug) as CategoryPageData | null

    const entry = findRegistryEntry(service, slug)
    if (!entry) {
        if (service.type === RenderedServicesType.widget) {
            return <DefaultWidgetFallback service={service} categoryName={slug} {...props} />
        }
        return null
    }

    const Component = entry.component
    const componentProps = buildComponentProps(
        entry,
        service,
        slug,
        props,
        categoryData,
        primaryService,
        primarySku,
        resolvedCategoryHeader,
        prioritizeFeaturedCategoryLcp,
    )
    if (!componentProps) return null

    return <Component {...componentProps} />
}

function buildComponentProps(
    entry: import('../componentRegistry').RegistryEntry,
    service: RenderedService,
    categoryName: string,
    commonProps: { pageLocation: PageLocation; additionalInformation: { categoryName: string } },
    categoryData: CategoryPageData | null,
    primaryService?: ServiceItem | null,
    primarySku?: Sku | null,
    resolvedCategoryHeader?: ResolvedCategoryHeaderDisplay | null,
    prioritizeFeaturedCategoryLcp = false,
): Record<string, unknown> | null {
    const data = entry.dataKey && categoryData ? categoryData[entry.dataKey] : undefined

    switch (entry.type) {
        case RenderedServicesType.categoryHeader:
        case RenderedServicesType.categoryHeaderWithSearch:
            return {
                categoryName,
                service,
                data,
                primaryService,
                primarySku,
            }
        case RenderedServicesType.contentAccordion:
        case RenderedServicesType.contentWithBulletList:
        case RenderedServicesType.categorySearch:
        case RenderedServicesType.howItWorks:
        case RenderedServicesType.generalAISm:
        case RenderedServicesType.categoryStats:
        case RenderedServicesType.testimonialUgc:
        case RenderedServicesType.testimonialReviewsV2:
        case RenderedServicesType.getStartedCta:
        case RenderedServicesType.faq:
            return buildWidgetProps(entry, service, categoryName, commonProps, data, primaryService, primarySku)
        case RenderedServicesType.tabbedCardSliderV2:
            return {
                categoryName,
                service,
                pageLocation: commonProps.pageLocation,
                additionalInformation: commonProps.additionalInformation,
                categoryData,
                resolvedCategoryHeader,
                primaryService,
                primarySku,
            }
        case RenderedServicesType.tabbedCardSlider:
            if (entry.predicate) {
                return {
                    categoryName,
                    service,
                    pageLocation: commonProps.pageLocation,
                    additionalInformation: commonProps.additionalInformation,
                    categoryData,
                    resolvedCategoryHeader,
                    primaryService,
                    primarySku,
                }
            }
            return {
                tabbedCardSlider: service,
                pageLocation: commonProps.pageLocation,
                additionalInformation: commonProps.additionalInformation,
            }
        case RenderedServicesType.carouselV2:
            return {
                categoryName,
                service,
                pageLocation: commonProps.pageLocation,
                additionalInformation: commonProps.additionalInformation,
                categoryData,
            }
        case RenderedServicesType.carousel:
            if (entry.predicate) {
                return {
                    categoryName,
                    service,
                    pageLocation: commonProps.pageLocation,
                    additionalInformation: commonProps.additionalInformation,
                    categoryData,
                }
            }
            return {
                cardType: service.cardType,
                services: service.services,
                ...commonProps,
            }
        case RenderedServicesType.featuredService:
            return { featuredService: service, ...commonProps }
        case RenderedServicesType.featuredCategoryRow:
            return {
                featuredCategoryRow: service,
                prioritizeFirstCardBackground: prioritizeFeaturedCategoryLcp,
            }
        case RenderedServicesType.categoryDirectory:
            return { categoryDirectory: service }
        case RenderedServicesType.shelfGroup:
            return { shelfGroup: service }
        case RenderedServicesType.categoryHero:
            return { categoryHero: service }
        case RenderedServicesType.categoryTextHeader:
            return { categoryName, service }
        case RenderedServicesType.stack:
            return { services: service.services, ...commonProps }
        case RenderedServicesType.solo:
            if (!service.services?.length) return null
            return { service: service.services[0], solo: true, ...commonProps }
        case RenderedServicesType.categoryGrid:
            return { cardType: service.cardType }
        case RenderedServicesType.adaptiveCarousel:
            return {
                cardType: service.cardType,
                services: service.services,
                ...commonProps,
            }
        case RenderedServicesType.widget:
            return buildWidgetProps(entry, service, categoryName, commonProps, data, primaryService, primarySku)
        default:
            return null
    }
}

function buildWidgetProps(
    entry: import('../componentRegistry').RegistryEntry,
    service: RenderedService,
    categoryName: string,
    commonProps: { pageLocation: PageLocation; additionalInformation: { categoryName: string } },
    data: unknown,
    primaryService?: ServiceItem | null,
    primarySku?: Sku | null,
): Record<string, unknown> {
    const base = { categoryName, service, ...commonProps, primaryService, primarySku }
    if (entry.cardType === ServiceItemCardType.categoryStats || entry.type === RenderedServicesType.categoryStats) {
        return { service, categoryName, data, primaryService, primarySku, ...commonProps }
    }
    return { ...base, data }
}

function DefaultWidgetFallback({
    service,
    categoryName,
    ...commonProps
}: {
    service: RenderedService
    categoryName: string
    pageLocation: PageLocation
    additionalInformation: { categoryName: string }
}) {
    const StoreWidget = require('@/components/Store/StoreWidget').default
    const ClientBoundary = require('@/components/shared/ClientBoundary').default
    return (
        <ClientBoundary>
            <StoreWidget cardType={service.cardType} {...commonProps} />
        </ClientBoundary>
    )
}
